import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useMediaQuery, Grid } from "@mui/material";
import { logger } from "core/utils/logger";
import { useParams, useSearchParams, generatePath, useNavigate } from "react-router-dom";
import { deleteBooking, getBookingsList } from "services/bookings";
import { useSnackbar } from "notistack";
import PageTitle from "components/PageTitle";
import BookingsDesktopView from "./DesktopView";
import ScrollDialog from "components/ScrollDialog";
import AddBooking from "./AddBooking";
import useConfirm from "core/hooks/useConfirm";
import BookingsMobileView from "./MobileView";
import { ROUTES } from "routes/constants";
import { AppContext } from "core/context";
import { isRolesPermitted } from "core/utils/common";

const BookingsPage = ({pageRef}) => {
  logger.info('[BookingsPage] mounting...');
  
  const [openAddBookingModal, setOpenAddBookingModal] = useState(false);
  const {enqueueSnackbar} = useSnackbar();
  const [bookings, setBookings] = useState([]);
  const [bookingData, setBookingData] = useState(null);
  const {businessId} = useParams();
  const confirm = useConfirm();
  const [urlSearchParams, setURLSearchParams] = useSearchParams();

  const navigate = useNavigate();
  const isMinimumDesktop = useMediaQuery(theme => theme.breakpoints.up('md'));

  // Permissions
  const { permissions } = useContext(AppContext);
  const isAddBookingsAllowed = useMemo(() => isRolesPermitted(['Add bookings'], permissions), [permissions]);
  const isEditBookingsAllowed = useMemo(() => isRolesPermitted(['Edit bookings'], permissions), [permissions]);
  
  const getBookings = useCallback((status) => {
    logger.debug('[BookingsPage]:{getBookings} fetching bookings data...');
    const controller = new AbortController();

    getBookingsList(businessId, {signal: controller.signal, pageRef}).then(({response, error}) => {
      if(error?.message === 'canceled') return;
      // Return if any error
      if(error){
        setBookings([]);
        return enqueueSnackbar(error?.message, {variant: 'error'});
      }
      setBookings(response);
    });
    return controller;
  }, [pageRef, businessId, enqueueSnackbar]);

  useEffect(() => {
    const controller = getBookings();
    return () => {
      controller.abort();
    }
  }, [getBookings]);
  
  // Refresh data with current state
  const refreshData = useCallback(() => {
    getBookings();
  }, [getBookings]);
  
  const onModalClose = useCallback(() => {
    setOpenAddBookingModal(false);
    setBookingData(null);
    if(urlSearchParams.get('o') === 't'){
      urlSearchParams.delete('o');
      urlSearchParams.delete('bs');
      setURLSearchParams(urlSearchParams, {replace: true});
    }
  }, [setURLSearchParams, urlSearchParams]);

  const editBooking = useCallback((data) => {
    setBookingData(data);
    setOpenAddBookingModal(true);
  }, []);

  const cancelBooking = useCallback((bookingId) => {
    logger.debug('[BookingsPage]:{cancelBooking} deleting booking data');
    deleteBooking(businessId, bookingId, {pageRef}).then(({error}) => {
      // Return if any error
      if(error)return enqueueSnackbar(error?.message, {variant: 'error'});

      enqueueSnackbar('Booking has been cancelled.', {variant: 'success'});
      setBookings(bookings => bookings.filter(booking => booking.id !== bookingId));
    });
  }, [enqueueSnackbar, pageRef, businessId]);
  

  const onDeleteBtnClick = useCallback((bookingId) => {
    confirm({ title: '', description: "Are you sure you want to cancel this booking?" })
    .then(() => {
      cancelBooking(bookingId);
    })
    .catch(() => {});
  }, [confirm, cancelBooking]);

  useEffect(() => {
    if(urlSearchParams.get('o') === 't'){
      setOpenAddBookingModal(true);
    }
  }, [urlSearchParams]);

  const checkInInmate = useCallback((inmateData) => {
    const { name, mobileNumber, email='', joiningDate, bedId, sharingType , bedInfo } = inmateData || {};
    let bs='';
    const inmateDetails = {name, mobileNumber, email, joiningDate};

    if(bedId && sharingType && bedInfo){
      bs = { sharingType, bedInfo, bedId };
      bs = `&bs=${encodeURIComponent(JSON.stringify(bs))}`;
    }
    const inmateDetailsStr = encodeURIComponent(JSON.stringify(inmateDetails));
      navigate(`${generatePath(ROUTES.CHECK_IN, {businessId})}?iDfB=${inmateDetailsStr}${bs}`);
  }, [businessId, navigate]);
  
  
  return (
    <Grid container  marginBottom={3}>
      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
        <PageTitle title='Bookings' caption="Take bookings and block beds for prospective tenants"  />
      </Grid>
      {/* <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
        <BookingFilters onFilterStatusApplied={onFilterStatusApplied} />
      </Grid> */}
      <Grid item xs={12}>
        {isMinimumDesktop ? 
          <BookingsDesktopView 
            bookings={bookings} 
            addBooking={() => setOpenAddBookingModal(true)} 
            refreshData={refreshData}
            editBooking={editBooking}
            onDeleteBtnClick={onDeleteBtnClick}
            checkInInmate={checkInInmate}
            permissions={{isAddBookingsAllowed, isEditBookingsAllowed}}
          /> : 
          <BookingsMobileView 
            bookings={bookings} 
            addBooking={() => setOpenAddBookingModal(true)} 
            refreshData={refreshData}
            editBooking={editBooking}
            checkInInmate={checkInInmate}
            onDeleteBtnClick={onDeleteBtnClick}
            permissions={{isAddBookingsAllowed, isEditBookingsAllowed}}
          />
        }
      </Grid>
      <AddBookingModal businessId={businessId} onModalClose={onModalClose} openModal={openAddBookingModal} refreshData={refreshData} bookingData={bookingData} />
    </Grid>
  );
}

function AddBookingModal({openModal, onModalClose, businessId, refreshData, bookingData}) {

  const isMinimumMediumDesktop = useMediaQuery(theme => theme.breakpoints.up('sm'));

  return(
    <ScrollDialog 
      isFullScreen={isMinimumMediumDesktop ? false: true} 
      maxWidth="sm"  
      onModalClose={onModalClose} 
      open={openModal} 
      title={bookingData ? "Update Booking Details" : "New Booking"}
      handleClose={onModalClose}
      disableDialogActions={true}
      showCloseIcon={isMinimumMediumDesktop ?  true: false}
    >
      <AddBooking businessId={businessId} closeModal={onModalClose} onBookingAdded={refreshData} bookingData={bookingData} openModal={openModal} />
    </ScrollDialog>
  )
}
export default BookingsPage;
